'use client';

import { useTranslations } from 'next-intl';
import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '@/components/ui/Dialog';
import { TagChipInput } from './TagChipInput';

interface TagEditorDialogProps {
  open: boolean;
  /** Document label shown in the title, e.g. the filename. */
  label: string;
  /** Current tags of the document. */
  initialTags: string[];
  /** All tags known in the library, offered as quick suggestions. */
  knownTags?: string[];
  onClose: () => void;
  onSave: (tags: string[]) => void;
}

/**
 * Free-form tag editor for a single document. Tags drive scope membership, so
 * this is the precise tool for placing a document in (or out of) any scope. The
 * chip+input UX lives in the shared TagChipInput primitive.
 */
export function TagEditorDialog({
  open,
  label,
  initialTags,
  knownTags = [],
  onClose,
  onSave,
}: TagEditorDialogProps) {
  const t = useTranslations('documents');
  const [tags, setTags] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (open) setTags([...initialTags]);
  }, [open, initialTags]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogOverlay onClick={onClose} />
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('tagEditorDialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="truncate text-xs text-muted-foreground">{label}</p>
          <TagChipInput tags={tags} onChange={setTags} suggestions={knownTags} />
        </div>

        <DialogFooter>
          <button
            type="button"
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-150"
            onClick={onClose}
          >
            {t('tagEditorDialog.cancel')}
          </button>
          <button
            type="button"
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:bg-accent/90 transition-colors duration-150"
            onClick={() => {
              // TagChipInput commits any pending draft on blur, so `tags` is final.
              onSave(tags);
              onClose();
            }}
          >
            {t('tagEditorDialog.save')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
